import { useMemo, useState, useEffect } from "react";
import { TextField } from "@mui/material";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function EmailInput({
    value,
    onChange,
    onBlur,
    onValidationChange,
    fullWidth = true,
    label = "Email",
    autoComplete = "email",
    disabled = false,
    ...props
}) {
    const [touched, setTouched] = useState(false);

    const error = useMemo(() => {
        if (!touched || disabled) return "";

        const trimmedValue = value.trim();
        if (!trimmedValue) return "Email is required.";
        if (!emailRegex.test(trimmedValue)) return "Please enter a valid email address.";

        return "";
    }, [value, touched, disabled]);

    const isValid = useMemo(() => {
        if (disabled) return true; // Disabled fields are always considered valid
        const trimmedValue = value.trim();
        return trimmedValue && emailRegex.test(trimmedValue);
    }, [value, disabled]);

    // Notify parent of validation state changes
    useEffect(() => {
        onValidationChange?.(isValid);
    }, [isValid, onValidationChange]);

    const handleChange = (ev) => {
        onChange?.(ev.target.value);
    };

    const handleBlur = (ev) => {
        setTouched(true);
        const trimmedValue = ev.target.value.trim();
        onChange?.(trimmedValue);
        onBlur?.(trimmedValue);
    };

    return (
        <TextField
            type="email"
            label={label}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            error={Boolean(error)}
            helperText={error}
            autoComplete={autoComplete}
            required
            fullWidth={fullWidth}
            variant="outlined"
            disabled={disabled}
            slotProps={{
                input: {
                    inputMode: "email",
                    "aria-label": "Email Address"
                }
            }}
            {...props}
        />
    );
}